import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Subscription, timer } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
  private tokenEndpoint = environment.openId.refreshUrl;
  private clientId = environment.appClientId;
  private redirectUri = environment.openId.callbackUrl;
  private scopes = environment.openId.scope;

  private timerSub?: Subscription;

  constructor(private http: HttpClient) {}

  /**
   * Inicia um timer que dispara o refresh a cada 30 minutos.
   * Chame isso quando o usuário estiver autenticado (ex.: no AppComponent).
   */
  startFixedRefreshTimer(): void {
    // 30 minutos em milissegundos: 30 * 60 * 1000
    const thirtyMinutesMs = 30 * 60 * 1000;

    // Primeiro disparo após 30 min; depois repete a cada 30 min.
    this.timerSub = timer(thirtyMinutesMs, thirtyMinutesMs).subscribe(() => {
      this.refresh().catch((err) => {
        console.error('Falha ao atualizar token:', err);
      });
    });
  }

  /**
   * Realiza o refresh do token usando sessionStorage -> authnResult -> refresh_token
   */
  async refresh(): Promise<any> {
    const raw = sessionStorage.getItem('0-' + environment.appClientId);
    if (!raw) throw new Error('authnResult não encontrado no sessionStorage');

    let refreshToken: string | null = null;
    try {
      const authnResult = JSON.parse(raw);

      refreshToken = authnResult?.authnResult?.refresh_token ?? null;
    } catch {
      throw new Error('Falha ao parsear authnResult');
    }
    if (!refreshToken) throw new Error('refreshToken não encontrado');

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', this.clientId)
      .set('refresh_token', refreshToken)
      .set('scope', this.scopes)
      .set('redirect_uri', this.redirectUri);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    });

    const resp = await firstValueFrom(
      this.http.post(this.tokenEndpoint, body.toString(), { headers }),
    );

    // Atualiza sessionStorage com tokens retornados
    const stored = JSON.parse(
      sessionStorage.getItem('0-' + this.clientId) || '{}',
    );
    const data = resp as any;

    if (data.access_token) {
      stored.authnResult.access_token = data.access_token;
      stored.authzData = data.access_token;
    }
    if (data.refresh_token)
      stored.authnResult.refresh_token = data.refresh_token; // refresh pode rotacionar
    if (typeof data.expires_in === 'number') {
      stored.authnResult.expires_in = data.expires_in; // segundos
      stored.authnResult.expires_in = data.expires_in;
      stored.accessTokenExpiresAt = Date.now() + data.expires_in * 1000;
    }

    sessionStorage.setItem('0-' + this.clientId, JSON.stringify(stored));
    return resp;
  }

  stop(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = undefined;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
